import { useEffect, useRef } from 'react';

interface PreloadOptions {
  delay?: number;
  priority?: 'high' | 'low';
}

/**
 * Hook for intelligent route preloading
 * Preloads route components on hover/focus to improve perceived performance
 */
export function useRoutePreloader() {
  const preloadTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    // Cleanup timers on unmount
    return () => {
      preloadTimersRef.current.forEach(timer => clearTimeout(timer));
      preloadTimersRef.current.clear();
    };
  }, []);

  /**
   * Preload a lazy component after a delay (default 300ms hover)
   */
  const preloadRoute = (
    importFn: () => Promise<any>,
    routeKey: string,
    options: PreloadOptions = {}
  ) => {
    const { delay = 300, priority = 'low' } = options;

    // Cancel any existing timer for this route
    const existingTimer = preloadTimersRef.current.get(routeKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new preload timer
    const timer = setTimeout(() => {
      importFn()
        .then(() => {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.debug(`[RoutePreloader] Preloaded: ${routeKey}`);
          }
        })
        .catch((error) => {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn(`[RoutePreloader] Failed to preload ${routeKey}:`, error);
          }
        });
      preloadTimersRef.current.delete(routeKey);
    }, delay);

    preloadTimersRef.current.set(routeKey, timer);
  };

  /**
   * Cancel preloading for a specific route
   */
  const cancelPreload = (routeKey: string) => {
    const timer = preloadTimersRef.current.get(routeKey);
    if (timer) {
      clearTimeout(timer);
      preloadTimersRef.current.delete(routeKey);
    }
  };

  /**
   * Immediate preload (for critical routes)
   */
  const preloadImmediate = (importFn: () => Promise<any>, routeKey: string) => {
    preloadRoute(importFn, routeKey, { delay: 0, priority: 'high' });
  };

  return {
    preloadRoute,
    cancelPreload,
    preloadImmediate,
  };
}
